import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import BankReconciliation from './BankReconciliation';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

const SOURCE_MODULES = [
  { value: 'manual',       label: 'Manual Entry' },
  { value: 'payments',     label: 'Deposit Reconciliation' },
  { value: 'savings',      label: 'Savings Collection' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'loans',        label: 'Loan Repayment' },
  { value: 'fines',        label: 'Fine Payment' },
  { value: 'rosca',        label: 'ROSCA Contribution' },
  { value: 'expense',      label: 'Expense' },
  { value: 'donation',     label: 'Donation / Grant' },
  { value: 'transfer',     label: 'Account Transfer' },
];

const ACCOUNT_COLORS = ['#2980b9', '#27ae60', '#8e44ad'];

function Treasury() {
  const [accounts, setAccounts]           = useState([]);
  const [members, setMembers]             = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [summary, setSummary]             = useState({});
  const [tab, setTab]                     = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountTxns, setAccountTxns]     = useState([]);
  const [showForm, setShowForm]           = useState(false);
  const [showTransfer, setShowTransfer]   = useState(false);
  const [editingId, setEditingId]         = useState(null);
  const [editData, setEditData]           = useState({});
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Delete confirmation state — holds the full transaction object to be deleted
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);

  // Filters
  const [filterAccount, setFilterAccount]     = useState('');
  const [filterFrom, setFilterFrom]           = useState('');
  const [filterTo, setFilterTo]               = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterModule, setFilterModule]       = useState('');

  // Source toggle for new transaction: 'member' | 'manual'
  const [sourceType, setSourceType] = useState('member');

  const [form, setForm] = useState({
    account_id: '', direction: 'IN', amount: '', description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    source_module: 'manual', receipt_ref: '',
    member_id: '', manual_source: ''
  });

  const [transferForm, setTransferForm] = useState({
    from_account_id: '', to_account_id: '', amount: '',
    description: '', transfer_date: new Date().toISOString().split('T')[0]
  });
  const readOnly = isChairmanReadOnly();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const getUserRole = () => {
    try {
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1])).role;
    } catch { return null; }
  };
  const isAdmin = getUserRole() === 'admin';

  useEffect(() => {
    fetchAccounts();
    fetchSummary();
    fetchTransactions();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      axios.get(`${API}/transactions/account/${selectedAccount}`, { headers })
        .then(r => setAccountTxns(r.data)).catch(() => {});
    }
  }, [selectedAccount]);

  const fetchAccounts = () => {
    axios.get(`${API}/accounts`, { headers }).then(r => setAccounts(r.data)).catch(() => {});
  };

  const fetchSummary = () => {
    axios.get(`${API}/summary`, { headers }).then(r => setSummary(r.data)).catch(() => {});
  };

  const fetchTransactions = () => {
    const params = {};
    if (filterAccount)   params.account_id   = filterAccount;
    if (filterFrom)      params.from_date     = filterFrom;
    if (filterTo)        params.to_date       = filterTo;
    if (filterDirection) params.direction     = filterDirection;
    if (filterModule)    params.source_module = filterModule;
    axios.get(`${API}/transactions`, { headers, params })
      .then(r => setTransactions(r.data)).catch(() => {});
  };

  const resetForm = () => {
    setForm({
      account_id: '', direction: 'IN', amount: '', description: '',
      transaction_date: new Date().toISOString().split('T')[0],
      source_module: 'manual', receipt_ref: '',
      member_id: '', manual_source: ''
    });
    setSourceType('member');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    const payload = {
      ...form,
      member_id:     sourceType === 'member' ? (form.member_id || null) : null,
      manual_source: sourceType === 'manual' ? (form.manual_source || null) : null,
    };
    try {
      await axios.post(`${API}/transactions`, payload, { headers });
      setSuccess('Transaction recorded');
      setShowForm(false);
      resetForm();
      fetchAccounts(); fetchSummary(); fetchTransactions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record transaction');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/transfer`, transferForm, { headers });
      setSuccess(res.data.message);
      setShowTransfer(false);
      setTransferForm({ from_account_id: '', to_account_id: '', amount: '', description: '', transfer_date: new Date().toISOString().split('T')[0] });
      fetchAccounts(); fetchSummary(); fetchTransactions();
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed');
    }
  };

  const saveEdit = async (id) => {
    setError(''); setSuccess('');
    try {
      await axios.patch(`${API}/transactions/${id}`, editData, { headers });
      setSuccess('Transaction updated');
      setEditingId(null);
      fetchAccounts(); fetchSummary(); fetchTransactions();
      if (selectedAccount) {
        axios.get(`${API}/transactions/account/${selectedAccount}`, { headers })
          .then(r => setAccountTxns(r.data)).catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  // ── DELETE ────────────────────────────────────────────────
  const confirmDelete = (txn) => {
    setDeleteTarget(txn);
    setError('');
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(''); setSuccess('');
    try {
      const res = await axios.delete(`${API}/transactions/${deleteTarget.id}`, { headers });
      setSuccess(res.data.message);
      setDeleteTarget(null);
      fetchAccounts(); fetchSummary(); fetchTransactions();
      if (selectedAccount) {
        axios.get(`${API}/transactions/account/${selectedAccount}`, { headers })
          .then(r => setAccountTxns(r.data)).catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };
  // ─────────────────────────────────────────────────────────

  const applyFilters = () => fetchTransactions();

  const clearFilters = () => {
    setFilterAccount(''); setFilterFrom(''); setFilterTo('');
    setFilterDirection(''); setFilterModule('');
    setTimeout(() => fetchTransactions(), 100);
  };

  const totalIn  = transactions.filter(t => t.direction === 'IN').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalOut = transactions.filter(t => t.direction === 'OUT').reduce((s, t) => s + parseFloat(t.amount), 0);

  // Derive account name for delete dialog
  const deleteTargetAccount = deleteTarget
    ? accounts.find(a => a.id == deleteTarget.account_id)?.account_name || '—'
    : '';

  return (
    <div style={{ padding: '20px' }}>

      {/* ── DELETE CONFIRMATION MODAL ── */}
      {deleteTarget && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#c0392b' }}>Delete Transaction</h3>
            </div>
            <p style={{ margin: '0 0 16px', color: '#2c3e50', lineHeight: '1.6' }}>
              This action is <strong>permanent</strong> and cannot be undone.
              The account balance will be adjusted accordingly.
            </p>
            <div style={{ background: '#fdf3f3', border: '1px solid #e74c3c', borderRadius: '6px', padding: '14px', marginBottom: '20px' }}>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Transaction ID</span><span>#{deleteTarget.id}</span></div>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Date</span><span>{deleteTarget.transaction_date?.slice(0, 10) || '—'}</span></div>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Account</span><span>{deleteTargetAccount}</span></div>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Direction</span>
                <span style={{ background: deleteTarget.direction === 'IN' ? '#27ae60' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                  {deleteTarget.direction === 'IN' ? 'Money In' : 'Money Out'}
                </span>
              </div>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Amount</span>
                <strong style={{ color: deleteTarget.direction === 'IN' ? '#27ae60' : '#e74c3c', fontSize: '15px' }}>
                  UGX {Number(deleteTarget.amount).toLocaleString()}
                </strong>
              </div>
              <div style={modalRowStyle}><span style={modalLabelStyle}>Description</span><span>{deleteTarget.description}</span></div>
              {deleteTarget.receipt_ref && (
                <div style={modalRowStyle}><span style={modalLabelStyle}>Receipt Ref</span><span>{deleteTarget.receipt_ref}</span></div>
              )}
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e74c3c', fontSize: '12px', color: '#c0392b', fontWeight: '600' }}>
                Balance impact: UGX {Number(deleteTarget.amount).toLocaleString()} will be{' '}
                {deleteTarget.direction === 'IN' ? 'subtracted from' : 'added back to'} {deleteTargetAccount}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteTarget(null)} style={btnStyle('#95a5a6')} disabled={deleting}>
                Cancel
              </button>
              {!readOnly && <button onClick={executeDelete} style={{ ...btnStyle('#c0392b'), minWidth: '120px' }} disabled={deleting}>
                {deleting ? 'Deleting…' : '🗑 Confirm Delete'}
              </button>}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Treasury Accounts</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!readOnly && <button onClick={() => { setShowTransfer(!showTransfer); setShowForm(false); }} style={btnStyle('#8e44ad')}>
            {showTransfer ? 'Cancel' : '⇄ Transfer'}
          </button>}
          {!readOnly && <button onClick={() => { setShowForm(!showForm); setShowTransfer(false); if (showForm) resetForm(); }} style={btnStyle('#2c3e50')}>
            {showForm ? 'Cancel' : '+ Record Transaction'}
          </button>}
        </div>
      </div>

      {/* Alerts */}
      {error   && <div style={alertStyle('#e74c3c')}>{error}<span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {/* Account Cards */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {accounts.map((a, i) => (
          <div key={a.id} onClick={() => { setSelectedAccount(a.id); setTab('by_account'); }}
            style={{ background: ACCOUNT_COLORS[i] || '#2c3e50', color: 'white', padding: '20px', borderRadius: '8px', minWidth: '200px', flex: 1, cursor: 'pointer', border: selectedAccount == a.id ? '3px solid white' : '3px solid transparent' }}>
            <div style={{ fontSize: '13px', opacity: 0.85 }}>{a.account_name}</div>
            <div style={{ fontSize: '26px', fontWeight: 'bold' }}>UGX {Number(a.balance).toLocaleString()}</div>
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Click to view ledger</div>
          </div>
        ))}
        <div style={{ background: '#2c3e50', color: 'white', padding: '20px', borderRadius: '8px', minWidth: '200px', flex: 1 }}>
          <div style={{ fontSize: '13px', opacity: 0.85 }}>Total Treasury</div>
          <div style={{ fontSize: '26px', fontWeight: 'bold' }}>UGX {Number(summary.total_balance || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* ── RECORD TRANSACTION FORM ── */}
      {!readOnly && showForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', width: '100%', color: '#2c3e50' }}>Record Transaction</h3>

          <div style={fieldStyle}>
            <label style={labelStyle}>Account *</label>
            <select style={inputStyle} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} required>
              <option value=''>-- Select --</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name} — UGX {Number(a.balance).toLocaleString()}</option>)}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Direction *</label>
            <select style={inputStyle} value={form.direction} onChange={e => setForm({ ...form, direction: e.target.value })}>
              <option value='IN'>Money In</option>
              <option value='OUT'>Money Out</option>
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.source_module} onChange={e => setForm({ ...form, source_module: e.target.value })}>
              {SOURCE_MODULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Amount (UGX) *</label>
            <input type='number' style={inputStyle} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
          </div>

          <div style={{ ...fieldStyle, minWidth: '250px' }}>
            <label style={labelStyle}>Description *</label>
            <input style={inputStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Receipt Ref</label>
            <input style={inputStyle} value={form.receipt_ref} onChange={e => setForm({ ...form, receipt_ref: e.target.value })} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Date</label>
            <input type='date' style={inputStyle} value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} />
          </div>

          {/* ── SOURCE FIELD ── */}
          <div style={{ width: '100%', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '14px 16px' }}>
            <label style={{ ...labelStyle, color: '#1a5276', marginBottom: '10px', display: 'block' }}>
              Source — Who is this money from / going to?
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button type="button"
                onClick={() => { setSourceType('member'); setForm({ ...form, manual_source: '' }); }}
                style={{
                  padding: '6px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: sourceType === 'member' ? '#2980b9' : '#ecf0f1',
                  color: sourceType === 'member' ? 'white' : '#7f8c8d'
                }}>
                👤 Select Member
              </button>
              <button type="button"
                onClick={() => { setSourceType('manual'); setForm({ ...form, member_id: '' }); }}
                style={{
                  padding: '6px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: sourceType === 'manual' ? '#e67e22' : '#ecf0f1',
                  color: sourceType === 'manual' ? 'white' : '#7f8c8d'
                }}>
                ✎ Manual Input
              </button>
            </div>

            {sourceType === 'member' && (
              <select style={inputStyle} value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })}>
                <option value=''>— None / Not applicable —</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>
                ))}
              </select>
            )}

            {sourceType === 'manual' && (
              <div>
                <input
                  style={inputStyle}
                  placeholder="e.g. Bank Interest, External Donation, Parish Council..."
                  value={form.manual_source}
                  onChange={e => setForm({ ...form, manual_source: e.target.value })}
                />
                <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>
                  Use for non-member sources — donations, grants, bank interest, etc.
                </div>
              </div>
            )}
          </div>

          <div style={{ width: '100%', display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button type='submit' style={btnStyle('#2c3e50')}>Save Transaction</button>
            <button type='button' onClick={() => { setShowForm(false); resetForm(); }} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {/* Transfer Form */}
      {!readOnly && showTransfer && (
        <form onSubmit={handleTransfer} style={{ ...formStyle, border: '2px solid #8e44ad' }}>
          <h3 style={{ margin: '0 0 15px', width: '100%', color: '#8e44ad' }}>Transfer Between Accounts</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>From Account *</label>
            <select style={inputStyle} value={transferForm.from_account_id} onChange={e => setTransferForm({ ...transferForm, from_account_id: e.target.value })} required>
              <option value=''>-- Select --</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name} — UGX {Number(a.balance).toLocaleString()}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>To Account *</label>
            <select style={inputStyle} value={transferForm.to_account_id} onChange={e => setTransferForm({ ...transferForm, to_account_id: e.target.value })} required>
              <option value=''>-- Select --</option>
              {accounts.filter(a => a.id != transferForm.from_account_id).map(a => <option key={a.id} value={a.id}>{a.account_name} — UGX {Number(a.balance).toLocaleString()}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Amount (UGX) *</label>
            <input type='number' style={inputStyle} value={transferForm.amount} onChange={e => setTransferForm({ ...transferForm, amount: e.target.value })} required />
          </div>
          <div style={{ ...fieldStyle, minWidth: '250px' }}>
            <label style={labelStyle}>Description</label>
            <input style={inputStyle} value={transferForm.description} onChange={e => setTransferForm({ ...transferForm, description: e.target.value })} placeholder='Optional — auto-generated if blank' />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Date</label>
            <input type='date' style={inputStyle} value={transferForm.transfer_date} onChange={e => setTransferForm({ ...transferForm, transfer_date: e.target.value })} />
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type='submit' style={btnStyle('#8e44ad')}>Execute Transfer</button>
            <button type='button' onClick={() => setShowTransfer(false)} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
        {[['all', 'All Transactions'], ['by_account', 'Account Ledger'], ['bank_reconciliation', 'Bank Reconciliation']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
            background: tab === key ? '#2c3e50' : 'transparent',
            color: tab === key ? 'white' : '#7f8c8d',
            borderRadius: '4px 4px 0 0'
          }}>{label}</button>
        ))}
      </div>

      {/* ── ALL TRANSACTIONS TAB ── */}
      {tab === 'all' && (
        <div>
          {/* Filters */}
          <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Account</label>
              <select style={inputStyle} value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
                <option value=''>All Accounts</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Direction</label>
              <select style={inputStyle} value={filterDirection} onChange={e => setFilterDirection(e.target.value)}>
                <option value=''>All</option>
                <option value='IN'>Money In</option>
                <option value='OUT'>Money Out</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Category</label>
              <select style={inputStyle} value={filterModule} onChange={e => setFilterModule(e.target.value)}>
                <option value=''>All</option>
                {SOURCE_MODULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>From Date</label>
              <input type='date' style={inputStyle} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>To Date</label>
              <input type='date' style={inputStyle} value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={applyFilters} style={btnStyle('#2c3e50')}>Filter</button>
              <button onClick={clearFilters} style={btnStyle('#95a5a6')}>Clear</button>
            </div>
          </div>

          {!filterAccount && (
            <div style={{ background: '#fff8e1', border: '1px solid #f0d060', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', color: '#8a6d00' }}>
              Running Balance shown below is per-account — filter to a single Account above for a clean, uninterrupted running total. With "All Accounts" selected, each row still shows its own account's correct balance, but rows from different accounts are interleaved by date.
            </div>
          )}

          {transactions.length > 0 && (
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ background: '#eafaf1', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>
                Money In: <strong style={{ color: '#27ae60' }}>UGX {totalIn.toLocaleString()}</strong>
              </div>
              <div style={{ background: '#fdf3f3', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>
                Money Out: <strong style={{ color: '#e74c3c' }}>UGX {totalOut.toLocaleString()}</strong>
              </div>
              <div style={{ background: '#f0f4ff', padding: '10px 16px', borderRadius: '6px', fontSize: '13px' }}>
                Net: <strong style={{ color: totalIn - totalOut >= 0 ? '#27ae60' : '#e74c3c' }}>UGX {(totalIn - totalOut).toLocaleString()}</strong>
              </div>
              <div style={{ background: '#f9f9f9', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', color: '#95a5a6' }}>
                {transactions.length} records
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Account</th>
                  <th style={thStyle}>Category</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Debit (UGX)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Credit (UGX)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Running Balance (UGX)</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Receipt</th>
                  <th style={thStyle}>Member / Source</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr><td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No transactions</td></tr>
                )}
                {transactions.map((t, i) => (
                  editingId === t.id ? (
                    <tr key={t.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={tdStyle}>
                        <input type="date" value={editData.transaction_date} onChange={e => setEditData({ ...editData, transaction_date: e.target.value })} style={inlineInput} />
                      </td>
                      <td style={tdStyle}>{t.account_name}</td>
                      <td style={tdStyle}>
                        <select value={editData.source_module} onChange={e => setEditData({ ...editData, source_module: e.target.value })} style={inlineInput}>
                          {SOURCE_MODULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle} colSpan={2}>
                        <input
                          type="number"
                          value={editData.amount}
                          onChange={e => setEditData({ ...editData, amount: e.target.value })}
                          style={{ ...inlineInput, textAlign: 'right', minWidth: '110px' }}
                        />
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                          {t.direction === 'IN' ? 'Credit' : 'Debit'} — direction cannot be changed here
                        </div>
                      </td>
                      <td style={tdStyle}>—</td>
                      <td style={tdStyle}>
                        <input value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} style={inlineInput} />
                      </td>
                      <td style={tdStyle}>
                        <input value={editData.receipt_ref || ''} onChange={e => setEditData({ ...editData, receipt_ref: e.target.value })} style={inlineInput} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <select value={editData.member_id || ''} onChange={e => setEditData({ ...editData, member_id: e.target.value, manual_source: '' })} style={inlineInput}>
                            <option value=''>— No member —</option>
                            {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                          </select>
                          <input
                            value={editData.manual_source || ''}
                            onChange={e => setEditData({ ...editData, manual_source: e.target.value, member_id: '' })}
                            placeholder="or manual source..."
                            style={inlineInput}
                          />
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {!readOnly && <button onClick={() => saveEdit(t.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                        <button onClick={() => setEditingId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={t.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{t.transaction_date ? t.transaction_date.slice(0, 10) : '—'}</td>
                      <td style={tdStyle}>{t.account_name}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: '11px', color: '#666', background: '#ecf0f1', padding: '2px 6px', borderRadius: '4px' }}>
                          {SOURCE_MODULES.find(s => s.value === t.source_module)?.label || t.source_module || 'Manual'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c', fontWeight: t.direction === 'OUT' ? 'bold' : 'normal' }}>
                        {t.direction === 'OUT' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#27ae60', fontWeight: t.direction === 'IN' ? 'bold' : 'normal' }}>
                        {t.direction === 'IN' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: parseFloat(t.running_balance) >= 0 ? '#2c3e50' : '#e74c3c' }}>
                        {Number(t.running_balance || 0).toLocaleString()}
                      </td>
                      <td style={tdStyle}>{t.description}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', color: '#95a5a6' }}>{t.receipt_ref || '—'}</td>
                      <td style={tdStyle}>
                        {t.member_name ? (
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1a5276' }}>👤 {t.member_name}</span>
                        ) : t.manual_source ? (
                          <span style={{ fontSize: '12px', color: '#e67e22' }}>✎ {t.manual_source}</span>
                        ) : (
                          <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {!readOnly && <button onClick={() => {
                            setEditingId(t.id);
                            setEditData({
                              description:      t.description,
                              receipt_ref:      t.receipt_ref || '',
                              transaction_date: t.transaction_date?.slice(0, 10) || '',
                              source_module:    t.source_module || 'manual',
                              member_id:        t.member_id || '',
                              manual_source:    t.manual_source || '',
                              amount:           t.amount
                            });
                          }} style={smBtn('#e67e22')}>Edit</button>}

                          {/* Delete — admin only */}
                          {!readOnly && isAdmin && (
                            <button
                              onClick={() => confirmDelete(t)}
                              style={smBtn('#c0392b')}
                              title="Delete transaction and reverse balance"
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ACCOUNT LEDGER TAB ── */}
      {tab === 'by_account' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
            <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Select Account:</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {accounts.map((a, i) => (
                <button key={a.id} onClick={() => setSelectedAccount(a.id)} style={{
                  padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  background: selectedAccount == a.id ? ACCOUNT_COLORS[i] : '#ecf0f1',
                  color: selectedAccount == a.id ? 'white' : '#2c3e50',
                  fontWeight: selectedAccount == a.id ? 'bold' : 'normal'
                }}>{a.account_name}</button>
              ))}
            </div>
          </div>

          {selectedAccount && (
            <>
              <div style={{ background: ACCOUNT_COLORS[accounts.findIndex(a => a.id == selectedAccount)] || '#2c3e50', color: 'white', padding: '16px 20px', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', opacity: 0.85 }}>{accounts.find(a => a.id == selectedAccount)?.account_name} — Current Balance</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                  UGX {Number(accounts.find(a => a.id == selectedAccount)?.balance || 0).toLocaleString()}
                </div>
              </div>

              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Category</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Debit (UGX)</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Credit (UGX)</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Member / Source</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {accountTxns.length === 0 && (
                    <tr><td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No transactions for this account</td></tr>
                  )}
                  {accountTxns.map((t, i) => (
                    <tr key={t.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{t.transaction_date ? t.transaction_date.slice(0, 10) : '—'}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: '11px', color: '#666', background: '#ecf0f1', padding: '2px 6px', borderRadius: '4px' }}>
                          {SOURCE_MODULES.find(s => s.value === t.source_module)?.label || t.source_module || 'Manual'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#e74c3c', fontWeight: t.direction === 'OUT' ? 'bold' : 'normal' }}>
                        {t.direction === 'OUT' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#27ae60', fontWeight: t.direction === 'IN' ? 'bold' : 'normal' }}>
                        {t.direction === 'IN' ? Number(t.amount).toLocaleString() : '—'}
                      </td>
                      <td style={tdStyle}>{t.description}</td>
                      <td style={tdStyle}>
                        {t.member_name ? (
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1a5276' }}>👤 {t.member_name}</span>
                        ) : t.manual_source ? (
                          <span style={{ fontSize: '12px', color: '#e67e22' }}>✎ {t.manual_source}</span>
                        ) : (
                          <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: parseFloat(t.running_balance) >= 0 ? '#2c3e50' : '#e74c3c' }}>
                        UGX {Number(t.running_balance || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {!selectedAccount && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
              Select an account above to view its ledger with running balance
            </div>
          )}
        </div>
      )}

      {/* ── BANK RECONCILIATION TAB ── */}
      {tab === 'bank_reconciliation' && <BankReconciliation accounts={accounts} />}
    </div>
  );
}

const btnStyle    = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' });
const smBtn       = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const formStyle   = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle  = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' };
const labelStyle  = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle  = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #f39c12', fontSize: '12px', background: '#fffde7', width: '100%' };
const tableStyle  = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle     = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle     = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle  = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });

// Modal styles
const overlayStyle  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalStyle    = { background: 'white', borderRadius: '10px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' };
const modalRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '13px' };
const modalLabelStyle = { color: '#7f8c8d', fontWeight: '600', minWidth: '120px' };

export default Treasury;